import { createListenerMiddleware } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { InlineEditResolvedAction } from '@/analytics/enums/inline-edit.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { getArtifactEditorSession } from '@/analytics/utils/artifact-editor-session'
import { AgentV2MatterArtifactEditProposalActions } from '@/agent-v2/artifact-edit-review/store/slice'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { MatterArtifactEditOperation } from '@/matter-artifacts/schemas'
import { kAgentV2InlineEditPresetInstructions } from '@/agent-v2/artifact-selection/inline-edit-presets'

/**
 * Inline edit analytics.
 *
 * Observational only: this middleware reacts to actions the selection and edit-review flows
 * already dispatch, and never dispatches back.
 *
 * Mint a client-side correlation id at submit because the proposal does not exist yet.
 * At most one proposal can be pending per conversation.
 */
type InFlightInlineEdit = {
  inlineEditId: string
  submittedAt: number
  matterArtifactVersionId: string
  sessionId: string
  /** Digest of the selected passage, so the proposal can be compared without keeping the text. */
  selectedPassageDigest: string
  /** Whether the proposal came back identical to the selection; resolved when it arrives. */
  isNoDiff?: boolean
}

/**
 * Non-cryptographic digest used only to tell whether a proposal came back identical to the passage
 * it replaces. Analytics keeps this instead of the passage itself: both are user-authored legal
 * text, and the payload never carries them. A collision would at worst mislabel one resolution.
 */
function digestPassage(passage: string): string {
  let hash = 0
  for (let index = 0; index < passage.length; index += 1) {
    hash = (hash << 5) - hash + passage.charCodeAt(index)
    hash |= 0
  }
  return `${passage.length}:${hash}`
}

const inFlightByConversationId = new Map<string, InFlightInlineEdit>()

export const agentV2InlineEditAnalyticsMiddleware = createListenerMiddleware()
const start = agentV2InlineEditAnalyticsMiddleware.startListening

start({
  actionCreator: AgentV2TranscriptActions.turnSubmitted,
  effect: (action) => {
    const { conversationId, inputText, matterArtifactSelection } = action.payload
    // An ordinary chat turn ends any inline edit still awaiting a decision: a proposal arriving
    // after it answers this turn, not the abandoned selection.
    if (!matterArtifactSelection) {
      inFlightByConversationId.delete(conversationId)
      return
    }

    const track = getTrackEventGlobal()
    if (!track) return

    const inFlight: InFlightInlineEdit = {
      inlineEditId: uuidv4(),
      submittedAt: Date.now(),
      matterArtifactVersionId: matterArtifactSelection.matter_artifact_version_id,
      selectedPassageDigest: digestPassage(matterArtifactSelection.selected_markdown),
      sessionId: getArtifactEditorSession(conversationId),
    }
    inFlightByConversationId.set(conversationId, inFlight)

    track(AnalyticsEvent.InlineEditSubmitted, {
      inlineEditId: inFlight.inlineEditId,
      artifactVersionId: inFlight.matterArtifactVersionId,
      conversationId,
      sessionId: inFlight.sessionId,
      selectionWordCount: matterArtifactSelection.selected_markdown.trim().split(/\s+/).filter(Boolean).length,
      selectionFrom: matterArtifactSelection.markdown_start_utf16,
      selectionTo: matterArtifactSelection.markdown_end_utf16,
      isPreset: kAgentV2InlineEditPresetInstructions.includes(inputText),
    })
  },
})

start({
  actionCreator: AgentV2MatterArtifactEditProposalActions.proposalReceived,
  effect: (action) => {
    const { conversationId } = action.payload
    const inFlight = inFlightByConversationId.get(conversationId)
    // An edit proposed without a tracked submit has nothing to correlate to.
    if (!inFlight) return

    const track = getTrackEventGlobal()
    if (!track) return

    track(AnalyticsEvent.InlineEditCompleted, {
      inlineEditId: inFlight.inlineEditId,
      artifactVersionId: inFlight.matterArtifactVersionId,
      conversationId,
      sessionId: inFlight.sessionId,
      inlineEditSuccess: true,
      durationMs: Date.now() - inFlight.submittedAt,
    })

    // Resolve the no-op verdict now, against the digest, and keep only the boolean.
    inFlightByConversationId.set(conversationId, {
      ...inFlight,
      isNoDiff: digestPassage(action.payload.proposal.replacement_markdown) === inFlight.selectedPassageDigest,
    })
  },
})

function resolveInlineEdit(conversationId: string, resolution: InlineEditResolvedAction) {
  const inFlight = inFlightByConversationId.get(conversationId)
  if (!inFlight) return

  inFlightByConversationId.delete(conversationId)

  const track = getTrackEventGlobal()
  if (!track) return

  track(AnalyticsEvent.InlineEditResolved, {
    inlineEditId: inFlight.inlineEditId,
    artifactVersionId: inFlight.matterArtifactVersionId,
    conversationId,
    sessionId: inFlight.sessionId,
    inlineEditResolution: resolution,
  })
}

start({
  actionCreator: AgentV2MatterArtifactEditProposalActions.acceptanceRequested,
  effect: (action) => {
    const { conversationId, operation } = action.payload
    const inFlight = inFlightByConversationId.get(conversationId)
    if (!inFlight) return

    if (operation === MatterArtifactEditOperation.INSERT_AFTER) {
      resolveInlineEdit(conversationId, InlineEditResolvedAction.InsertedBelow)
      return
    }

    resolveInlineEdit(conversationId, inFlight.isNoDiff ? InlineEditResolvedAction.NoDiff : InlineEditResolvedAction.Replaced)
  },
})

start({
  actionCreator: AgentV2MatterArtifactEditProposalActions.proposalRejected,
  effect: (action) => resolveInlineEdit(action.payload.conversationId, InlineEditResolvedAction.Declined),
})
